/** Pippi's shared state, read by the top-bar pill, the panel and the bubble (provided by PippiProvider). */
import { createContext, useContext, type Dispatch } from 'react';
import type { CaseCard, ConversationView, DeviceInfo, DraftCard, FileKind, PippiCase, PippiFile, PippiItem, WorkNote } from './pippiApi';
import type { UiAction, UiState } from './pippiState';

export interface StreamState { active: boolean; text: string; working: string | null; notes: WorkNote[]; cards: CaseCard[]; drafts: DraftCard[]; pendingUser: PippiItem | null }

export interface PippiValue {
  available: boolean;
  firstName: string;
  /** Staff (BO) or client (the coming Omni beta): copy and actions differ a little. */
  audience: 'staff' | 'client';
  ui: UiState;
  dispatch: Dispatch<UiAction>;
  conversation: ConversationView | null;
  items: PippiItem[];
  stream: StreamState;
  draft: string;
  setDraft: (s: string) => void;
  pending: PippiFile[];
  removePending: (id: string) => void;
  busy: string | null;
  error: string | null;
  errorCode: string | null;
  clearError: () => void;
  unread: number;
  device: DeviceInfo;
  cases: PippiCase[] | null;
  loadCases: () => Promise<void>;
  send: (text?: string) => Promise<void>;
  /** The person's OK on Pippi's draft (the card's button): the only way a draft is filed from the panel. */
  confirmDraft: (draftId: string, kind: 'case' | 'escalation') => Promise<void>;
  attachFiles: (files: Array<File | { blob: Blob; name: string }>, kind?: FileKind) => Promise<void>;
  takeScreenshot: () => Promise<void>;
  startRecording: (kind: 'screen' | 'show-me') => Promise<void>;
  stopRecording: () => Promise<void>;
  recording: { kind: 'screen' | 'show-me' | null; hasVideo: boolean; hasVoice: boolean; heard: string };
  dictation: { active: boolean; partial: string; toggle: () => void };
  /** "New report": back to a blank start. */
  newConversation: () => void;
  /** Opens a case's conversation on purpose (My cases): what is written next goes to that case. */
  openConversation: (id: string) => Promise<void>;
  openCase: (seq: number) => void;
  /** "Add more detail" on the case card: write into the filed conversation, on purpose. */
  addDetail: () => void;
  /** The conversation on screen is filed and was reopened on purpose: messages go to its case. */
  adding: boolean;
  /** The conversation on screen is filed: the next message starts a new report. */
  filed: boolean;
}

export const PippiContext = createContext<PippiValue | null>(null);

/** Null outside the provider (or before the gate answered): render nothing then. */
export const usePippi = () => useContext(PippiContext);
